import { useStore } from "@/store";
import {
  GoogleAnalyticsIntegration,
  GoogleSearchConsoleIntegration,
} from "@/store/slices/domainIntegrationsSlice";
import { useCallback } from "react";

export const useDomainIntegrations = (
  orgId: string | null,
  domainId: string | null,
) => {
  const {
    updateGoogleAnalyticsIntegration,
    updateGoogleSearchConsoleIntegration,
    deleteGoogleSearchConsoleIntegration,
    isLoadingDomainIntegrations,
    domainIntegrationsError,
  } = useStore();

  // Note: Subscription to domain integrations is handled centrally in useStoreInitializer

  // Read the current selections through a *selector*, not by calling the
  // store's getter during render: the getter reads `get()` behind a stable
  // function identity, so the React Compiler caches its result for the whole
  // mount and the UI keeps rendering the pre-connect state until it remounts.
  // A selector is a hook call, which the compiler always re-runs.
  const googleAnalyticsIntegration = useStore((state) =>
    state.getCurrentGoogleAnalyticsIntegration(),
  );
  const googleSearchConsoleIntegration = useStore((state) =>
    state.getCurrentGoogleSearchConsoleIntegration(),
  );

  // Update Google Analytics integration
  const updateGoogleAnalytics = useCallback(
    async (
      selectedAccountId: string | null,
      selectedPropertyId: string | null,
      display?: { accountEmail: string | null; propertyName: string | null },
    ) => {
      if (!orgId || !domainId) {
        console.error(
          "Cannot update Google Analytics integration: missing orgId or domainId",
        );
        return;
      }
      const integration: Partial<GoogleAnalyticsIntegration> = {
        selectedAccountId,
        selectedPropertyId,
        // Set is_enabled to true by default when establishing a connection
        ...(selectedAccountId && selectedPropertyId
          ? {
              is_enabled: true,
              connectedAccountEmail: display?.accountEmail ?? null,
              connectedPropertyName: display?.propertyName ?? null,
            }
          : {}),
      };

      try {
        await updateGoogleAnalyticsIntegration(orgId, domainId, integration);
        console.log(
          "Domain Integrations Hook: Updated Google Analytics integration:",
          integration,
        );
      } catch (error) {
        console.error(
          "Domain Integrations Hook: Failed to update Google Analytics integration:",
          error,
        );
        throw error;
      }
    },
    [orgId, domainId, updateGoogleAnalyticsIntegration],
  );

  const setGoogleAnalyticsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!orgId || !domainId) {
        console.error(
          "Cannot update Google Analytics enabled state: missing orgId or domainId",
        );
        return;
      }
      try {
        await updateGoogleAnalyticsIntegration(orgId, domainId, {
          is_enabled: enabled,
        });
      } catch (error) {
        console.error("Failed to set Google Analytics enabled state:", error);
        throw error;
      }
    },
    [orgId, domainId, updateGoogleAnalyticsIntegration],
  );

  const updateGoogleSearchConsole = useCallback(
    async (
      selectedAccountId: string | null,
      selectedSiteUrl: string | null,
      display?: { accountEmail: string | null },
    ) => {
      if (!orgId || !domainId) {
        console.error(
          "Cannot update Google Search Console integration: missing orgId or domainId",
        );
        return;
      }
      const integration: Partial<GoogleSearchConsoleIntegration> = {
        selectedAccountId,
        selectedSiteUrl,
        ...(selectedAccountId && selectedSiteUrl
          ? {
              is_enabled: true,
              connectedAccountEmail: display?.accountEmail ?? null,
            }
          : {}),
      };

      try {
        await updateGoogleSearchConsoleIntegration(
          orgId,
          domainId,
          integration,
        );
      } catch (error) {
        console.error(
          "Domain Integrations Hook: Failed to update Google Search Console integration:",
          error,
        );
        throw error;
      }
    },
    [orgId, domainId, updateGoogleSearchConsoleIntegration],
  );

  const setGoogleSearchConsoleEnabled = useCallback(
    async (enabled: boolean) => {
      if (!orgId || !domainId) {
        console.error(
          "Cannot update Google Search Console enabled state: missing orgId or domainId",
        );
        return;
      }
      try {
        await updateGoogleSearchConsoleIntegration(orgId, domainId, {
          is_enabled: enabled,
        });
      } catch (error) {
        console.error(
          "Failed to set Google Search Console enabled state:",
          error,
        );
        throw error;
      }
    },
    [orgId, domainId, updateGoogleSearchConsoleIntegration],
  );

  const deleteGoogleSearchConsole = useCallback(async () => {
    if (!orgId || !domainId) {
      console.error(
        "Cannot delete Google Search Console integration: missing orgId or domainId",
      );
      return;
    }
    try {
      await deleteGoogleSearchConsoleIntegration(orgId, domainId);
    } catch (error) {
      console.error(
        "Domain Integrations Hook: Failed to delete Google Search Console integration:",
        error,
      );
      throw error;
    }
  }, [orgId, domainId, deleteGoogleSearchConsoleIntegration]);

  return {
    // State
    isLoading: isLoadingDomainIntegrations,
    error: domainIntegrationsError,

    // Google Analytics integration
    googleAnalyticsIntegration,
    googleSearchConsoleIntegration,

    // Actions
    updateGoogleAnalytics,
    setGoogleAnalyticsEnabled,
    updateGoogleSearchConsole,
    setGoogleSearchConsoleEnabled,
    deleteGoogleSearchConsole,
  };
};
